import {useMutation} from '@tanstack/react-query';
import {UseFormReturn} from 'react-hook-form';
import {toast} from '@common/ui/toast/toast';
import {BackendResponse} from '@common/http/backend-response/backend-response';
import {onFormQueryError} from '@common/errors/on-form-query-error';
import {User} from '@common/auth/user';
import {message} from '@common/i18n/message';
import {apiClient} from '@common/http/query-client';
import {useAuth} from '@common/auth/use-auth';

interface Response extends BackendResponse {}

interface Payload {
  first_name?: string;
  last_name?: string;
  trustee_email?: string;
  trustee_name?: string;
  trustee_first_name?: string;
  trustee_last_name?: string;
  mobile_phone?: string;
}

export function useUpdateAccountDetails(form: UseFormReturn<Partial<User>>) {
  const {user} = useAuth();

  return useMutation({
    mutationFn: (props: Payload) => updateAccountDetails(props),
    onSuccess: (response, variables) => {
      toast(message('Updated account details'));

      // Check if trustee_email was updated and send notification
      if (variables.trustee_email && user && variables.trustee_email !== (user as any).trustee_email) {
        sendTrusteeNotification(variables.trustee_email, user.id, user.first_name, user.last_name);
      }
    },
    onError: r => onFormQueryError(r, form),
  });
}

function updateAccountDetails(payload: Payload): Promise<Response> {
  return apiClient.put('users/me', payload).then(r => r.data);
}

function sendTrusteeNotification(
  trusteeEmail: string,
  userId?: number,
  firstName?: string,
  lastName?: string
): Promise<void> {
  if (!userId) return Promise.resolve();

  return apiClient.post(`users/${userId}/add-trustee`, {
    trusteeEmail: trusteeEmail,
    firstName: firstName,
    lastName: lastName
  }).then(() => {
    toast(message('Trustee notification email sent'));
  }).catch(() => {
    toast(message('Failed to send trustee notification'));
  });
}
